import flatten from 'lodash/flatten';
import gettext from '@sdk/lang/gettext';
import {
  getTimesheetEntriesForProjection,
  parseTimesheetEntries,
  amendOrderOfEntries,
} from '@sdk/timesheets/helpers/utils';
import getAllBreaksLists from '@sdk/automatedBreaks/helpers/getAllBreaksLists';
import getScheduledBreaks from '@sdk/automatedBreaks/helpers/getScheduledBreaks';
import { mapAPIAutomatedBreaksSettingsToUI } from '@sdk/automatedBreaks/mappers';
import { AttestationOption } from '@sdk/automatedBreaks/types/Attestation';
import { AutomatedBreakType, AllBreakTypes } from '@sdk/settings/types';
import { AutomatedBreakPaidCategory } from '@sdk/automatedBreaks/types/AutomatedBreakPaidCategory';
import { TimesheetStatus } from '@sdk/timesheets/api/apiTypes';
import { isUnscheduledShift } from '@sdk/automatedBreaks/utils';
import { TimesheetEntryType } from '@sdk/timesheets/types/TimesheetEntry';

import timeclockNoteModalUrl from '@angular/timeclock/timeclockNoteModal.html';

const EMPTY_CELL_MARK = '';
const DASH_CELL_MARK = '-';

class TimesheetsService {
  constructor(
    Authentication,
    EventCollection,
    PersonaCollection,
    Restangular,
    Utils,
    sdk,
    toDurationFilter,
    extendShiftModel,
    extendUserModel,
    $uibModal,
    SlingApi,
    $q,
    uppercaseFirstFilter
  ) {
    'ngInject';

    Object.assign(this, {
      Authentication,
      EventCollection,
      PersonaCollection,
      Restangular,
      Utils,
      sdk,
      toDurationFilter,
      extendShiftModel,
      extendUserModel,
      $uibModal,
      SlingApi,
      $q,
      uppercaseFirstFilter,
    });
    this.calendarContext = sdk('context');
  }

  extendTimesheetModel = (_event) => {
    const event = this.extendShiftModel(_event);

    // hack to amend the order of entries
    // more info in the amendOrderOfEntries util
    const amendedEntriesPerProjection = event.timesheetProjections.map((projection) => {
      const entries = getTimesheetEntriesForProjection(projection, event.timesheetEntries);
      return amendOrderOfEntries(entries);
    });

    event.timesheetEntries = flatten(amendedEntriesPerProjection);
    // end hack

    if (!event.fromIntegration) {
      event.fromIntegration = 'Sling';
    }

    event.canBeSelected = () =>
      event.timesheetStatus === TimesheetStatus.pending &&
      this.Authentication.user.canActOnLocation(event.location) &&
      Boolean(event.timesheetProjections?.[event.timesheetProjections.length - 1]?.clockOut);

    event.clockedInArray = [];
    event.clockedOutArray = [];
    event.isAutoClockOutArray = [];
    event.breakTypeArray = [];
    event.breakStartArray = [];
    event.breakEndArray = [];
    event.breakDurationArray = [];
    event.scheduledBreakDurationArray = [];
    event.actualDurationArray = [];

    const breakDurationReducer = (agg, { breakDurationInMinutes }) =>
      agg + (breakDurationInMinutes || 0);

    const allBreaksLists = getAllBreaksLists(event.timesheetEntries, event.timesheetProjections);

    event.mealBreakDuration = allBreaksLists.reduce(
      (acc, breaksList) => acc + breaksList.meal.reduce(breakDurationReducer, 0),
      0
    );
    event.restBreakDuration = allBreaksLists.reduce(
      (acc, breaksList) => acc + breaksList.rest.reduce(breakDurationReducer, 0),
      0
    );
    this.accountPaidDurationForScheduledAutomatedBreaks(event);

    event.isPastDtendWithNoClockIn =
      !isUnscheduledShift(event) &&
      this.Utils.getNowDate() > event.dtendLocal &&
      !event.timesheetProjections.length;

    this.fillInForScheduledBreaks(event);

    event.timesheetProjections
      .map((projection) => getTimesheetEntriesForProjection(projection, event.timesheetEntries))
      .map(parseTimesheetEntries)
      .forEach((group, projectionIndex) => {
        if (projectionIndex > 0) {
          this.fillInMissingItemsInArrays(event);
          this.fillInForScheduledBreaks(event);
        }

        this.fillInClockAndBreakArrays(event, group, projectionIndex, allBreaksLists);
      });

    ['breakStartArray', 'breakEndArray', 'actualDurationArray'].forEach((prop) => {
      event[`${prop}_sortKey`] = event[prop].map((i) => i.value).find((i) => i !== EMPTY_CELL_MARK);
    });

    if (event.isPastDtendWithNoClockIn) {
      [
        'breakStartArray',
        'breakEndArray',
        'breakDurationArray',
        'actualDurationArray',
        'clockedInArray',
        'clockedOutArray',
      ].forEach((prop) => {
        event[prop] = [{ value: DASH_CELL_MARK, skipFormatting: true }];
      });
    }

    return event;
  };

  fillInMissingItemsInArrays(event) {
    const allArrayProps = [
      'clockedInArray',
      'clockedOutArray',
      'isAutoClockOutArray',
      'breakTypeArray',
      'breakStartArray',
      'breakEndArray',
      'breakDurationArray',
      'scheduledBreakDurationArray',
      'actualDurationArray',
    ];

    const maxItemsCount = Math.max(...allArrayProps.map((prop) => event[prop].length));

    allArrayProps.forEach((prop) => {
      for (let i = event[prop].length; i < maxItemsCount; i++) {
        event[prop].push({ value: EMPTY_CELL_MARK, skipFormatting: true });
      }
    });
  }

  fillInForScheduledBreaks(event) {
    const emptyDisplayValue = event.isPastDtendWithNoClockIn ? DASH_CELL_MARK : EMPTY_CELL_MARK;

    const automatedBreaksSettings = this.getAutomatedBreaksSettingsForEvent(event);
    if (automatedBreaksSettings.isEnabled) {
      const scheduledAutomatedBreaks = getScheduledBreaks({
        shift: event,
        automatedBreaksSettings,
      });

      scheduledAutomatedBreaks.typesOrder.forEach((automatedBreakType) => {
        if (automatedBreakType === AutomatedBreakType.meal) {
          event.breakStartArray.push({ value: emptyDisplayValue });
          event.breakEndArray.push({ value: emptyDisplayValue });
          event.breakDurationArray.push({ value: emptyDisplayValue });
          event.breakTypeArray.push({
            value: this.uppercaseFirstFilter(AllBreakTypes.meal),
            isMealBreak: true,
          });
          event.scheduledBreakDurationArray.push({
            value: scheduledAutomatedBreaks.mealBreakDuration,
          });
        } else {
          event.breakStartArray.push({ value: emptyDisplayValue });
          event.breakEndArray.push({ value: emptyDisplayValue });
          event.breakDurationArray.push({ value: emptyDisplayValue });
          event.breakTypeArray.push({
            value: this.uppercaseFirstFilter(AllBreakTypes.rest),
            isRestBreak: true,
          });
          event.scheduledBreakDurationArray.push({
            value: scheduledAutomatedBreaks.restBreakDuration,
          });
        }
      });
    } else {
      event.breakStartArray.push({ value: emptyDisplayValue });
      event.breakEndArray.push({ value: emptyDisplayValue });
      event.breakDurationArray.push({ value: emptyDisplayValue });
      event.breakTypeArray.push({ value: this.uppercaseFirstFilter(AllBreakTypes.standard) });
      event.scheduledBreakDurationArray.push({
        value: event.breakDuration ? event.breakDuration : emptyDisplayValue,
      });
    }
  }

  get(from, to) {
    return this.Authentication.sendRequest({ path: `timesheets?dates=${from}/${to}` })
      .then((data) => data.data)
      .then((data) => data.map(this.extendTimesheetModel));
  }

  bulkUpdate(timesheets) {
    return this.Authentication.sendRequest({
      method: 'put',
      path: 'timesheets/bulk',
      payload: timesheets,
    }).then((data) => data.data);
  }

  getTimesheets(from, to) {
    return this.Authentication.sendRequest({ path: `reports/timesheets?dates=${from}/${to}` })
      .then((data) => data.data)
      .then((data) => data.map(this.extendTimesheetModel));
  }

  updateTimesheets(shift, timesheet) {
    if (!shift.timesheets) {
      return;
    }

    const existingIndex = shift.timesheets.findIndex((t) => t.id === timesheet.id);
    const existing = shift.timesheets[existingIndex];

    if (existing && !timesheet.deleted) {
      Object.assign(existing, timesheet);
      return this.extendTimesheetModel(shift);
    }

    if (existing && timesheet.deleted) {
      shift.timesheets.splice(existingIndex, 1);
      return this.extendTimesheetModel(shift);
    }

    if (!timesheet.deleted) {
      shift.timesheets.push(timesheet);
      shift.timesheets = shift.timesheets.sort(
        (a, b) => this.Utils.getRealDate(a.clockIn) - this.Utils.getRealDate(b.clockIn)
      );
      return this.extendTimesheetModel(shift);
    }
  }

  joinSocketRoom() {
    this.calendarContext.webSocketService.send({
      command: 'join',
      ignore: this.calendarContext.authorizationToken,
      room: `timesheets:${this.Authentication.user.org.id}`,
    });
  }

  leaveSocketRoom() {
    this.calendarContext.webSocketService.send({
      command: 'leave',
      ignore: this.calendarContext.authorizationToken,
      room: `timesheets:${this.Authentication.user.org.id}`,
    });
  }

  async getHistory(shiftId) {
    return (await this.Authentication.sendRequest({ path: `timesheets/${shiftId}/history` })).data;
  }

  async approveTimesheets(ids, eventsToUpdate = []) {
    const { data } = await this.Authentication.sendRequest({
      method: 'post',
      path: 'timesheets/status/approved',
      payload: ids,
    });
    eventsToUpdate.forEach((event) => this.postTimesheetStatusChange(event, data));

    return data;
  }

  async markTimesheetsAsPending(ids, eventsToUpdate = []) {
    const { data } = await this.Authentication.sendRequest({
      method: 'post',
      path: 'timesheets/status/pending',
      payload: ids,
    });
    eventsToUpdate.forEach((event) => this.postTimesheetStatusChange(event, data));

    return data;
  }

  postTimesheetStatusChange(event, projections) {
    const dict = projections.reduce((acc, p) => ((acc[p.id] = p), acc), {});
    event.timesheetProjections.forEach((projection) => {
      if (projection.id in dict) {
        Object.assign(projection, dict[projection.id]);
      }
    });

    const updatedEvent = this.extendTimesheetModel(event);
    this.Utils.clearObject(event);
    Object.assign(event, updatedEvent);
  }

  async viewTimesheet({
    event: eventArg,
    defaultDate,
    mode = 'edit',
    _readonly = false,
    editNoteModeOnly = false,
  }) {
    const readonly = _readonly || mode === 'history';

    let event;
    if (eventArg) {
      event = this.extendShiftModel(
        await this.EventCollection.getTimesheetEvent(eventArg.id, eventArg.user.id)
      );
    }

    return this.$uibModal.open({
      template:
        '<edit-timesheets modal="modal" readonly="readonly" event="event" initial-mode="mode" default-date="defaultDate" edit-note-mode-only="editNoteModeOnly"></edit-timesheets>',
      size: '620',
      backdrop: readonly ? true : 'static',
      controller($scope, $uibModalInstance) {
        'ngInject';

        $scope.modal = $uibModalInstance;
        $scope.event = event;
        $scope.readonly = readonly;
        $scope.mode = mode;
        $scope.defaultDate = defaultDate;
        $scope.editNoteModeOnly = editNoteModeOnly;
      },
    }).result;
  }

  async addTimeclockNote(eventId, note) {
    const data = await this.SlingApi.addTimeclockNote(eventId, note);
    return data;
  }

  async viewTimesheetManagerMode(event) {
    const timesheet = this.extendShiftModel(
      await this.EventCollection.getTimesheetEvent(event.id, event.user.id)
    );
    if (this.modalInstance) {
      return this.modalInstance;
    }

    try {
      this.modalInstance = this.$uibModal.open({
        template: '<view-timesheet-modal modal="modal" event="event"></view-timesheet-modal>',
        size: '620',
        backdrop: true,
        controller($scope, $uibModalInstance) {
          'ngInject';

          $scope.modal = $uibModalInstance;
          $scope.event = timesheet;
        },
      });

      return await this.modalInstance.result;
    } finally {
      this.modalInstance = null;
    }
  }

  openTimeclockNoteModal(note, date, userFullName) {
    return this.$uibModal
      .open({
        templateUrl: timeclockNoteModalUrl,
        controllerAs: 'ctrl',
        size: '620',
        controller() {
          this.date = date;
          this.assigneeNotes = note;
          this.userFullName = userFullName;
        },
      })
      .result.ignoreCancel();
  }

  getTimeclockRadiusInMeters() {
    let radius = this.Authentication.user.orgSettings.clockInMaxDistance;
    if (this.Authentication.user.orgSettings.imperialUnits) {
      radius *= 0.3048; // feet to meters
    }

    return radius;
  }

  getDateValueObject = (value) => {
    const result = { value };

    if (value instanceof Date) {
      result.serializedDateValue = `${this.Utils.formatDate(value)}T${this.Utils.formatTime(
        value,
        false,
        true
      )}:00`;
    }

    return result;
  };

  fillInForStandardBreaks = (event, group, projectionStatus) => {
    const emptyValue =
      !projectionStatus || projectionStatus === TimesheetStatus.inProgress
        ? EMPTY_CELL_MARK
        : DASH_CELL_MARK;

    group.breaks.forEach(({ startEntry, endEntry, duration, type }, i) => {
      if (type !== null) {
        return;
      }

      let index = this._getBreakIndexInArray(event, AllBreakTypes.standard, i);

      if (index < 0) {
        event.breakTypeArray.push({ value: this.uppercaseFirstFilter(AllBreakTypes.standard) });
        event.scheduledBreakDurationArray.push({ value: EMPTY_CELL_MARK, skipFormatting: true });
        index = event.breakTypeArray.length - 1;
      }

      event.breakStartArray[index] = this.getDateValueObject(
        startEntry ? startEntry.timestamp : emptyValue
      );
      event.breakEndArray[index] = this.getDateValueObject(
        endEntry ? endEntry.timestamp : emptyValue
      );
      event.breakDurationArray[index] = {
        value: duration || emptyValue,
        skipFormatting: !duration && emptyValue === DASH_CELL_MARK,
      };
    });
  };

  _getBreakIndexInArray = (event, automatedBreakType, index) => {
    /**
     * "breakTypeArray" has both meal and rest breaks mixed together.
     * "index" denotes which index of type "automatedBreakType" do we need to match.
     * This function returns the index of that matched break in "breakTypeArray"
     */
    let j = 0;

    for (let k = 0; k < event.breakTypeArray.length; k++) {
      if (
        (event.breakTypeArray[k].isMealBreak && automatedBreakType === AutomatedBreakType.meal) ||
        (event.breakTypeArray[k].isRestBreak && automatedBreakType === AutomatedBreakType.rest) ||
        (!event.breakTypeArray[k].isRestBreak &&
          !event.breakTypeArray[k].isMealBreak &&
          automatedBreakType === AllBreakTypes.standard)
      ) {
        j++;
      }

      if (j === index + 1) {
        return k;
      }
    }

    return -1;
  };

  fillInForAutomatedBreaks = (event, allBreaksPerProjection, projectionStatus) => {
    const emptyValue =
      !projectionStatus || projectionStatus === TimesheetStatus.inProgress
        ? EMPTY_CELL_MARK
        : DASH_CELL_MARK;

    let rowsCount = Math.max(
      allBreaksPerProjection.meal.length,
      allBreaksPerProjection.rest.length
    );

    // Hide missed breaks for in progress shift
    if (projectionStatus === TimesheetStatus.inProgress) {
      const lastMealBreakIndex = allBreaksPerProjection.meal.findLastIndex(
        (breakItem) => !breakItem.isMissed
      );
      const lastRestBreakIndex = allBreaksPerProjection.rest.findLastIndex(
        (breakItem) => !breakItem.isMissed
      );

      rowsCount = Math.max(lastMealBreakIndex + 1, lastRestBreakIndex + 1);
    }

    const automatedBreaksSettings = this.getAutomatedBreaksSettingsForEvent(event);
    const scheduledAutomatedBreaks = getScheduledBreaks({ shift: event, automatedBreaksSettings });

    event.scheduledMealBreakDuration = scheduledAutomatedBreaks.mealTotalDuration;
    event.scheduledRestBreakDuration = scheduledAutomatedBreaks.restTotalDuration;

    for (let i = 0; i < rowsCount; i++) {
      [AutomatedBreakType.meal, AutomatedBreakType.rest].forEach((breakType) => {
        const automatedBreak = allBreaksPerProjection[breakType][i];
        if (!automatedBreak) {
          return;
        }

        let index = this._getBreakIndexInArray(event, breakType, i);

        if (index < 0) {
          event.breakTypeArray.push({ value: this.uppercaseFirstFilter(breakType) });
          event.scheduledBreakDurationArray.push({ value: DASH_CELL_MARK, skipFormatting: true });
          index = event.breakTypeArray.length - 1;
        }

        let breakDurationInMinutes = automatedBreak.breakDurationInMinutes;

        if (automatedBreak.attestationOption === AttestationOption.taken) {
          breakDurationInMinutes = automatedBreak.scheduledDurationInMinutes;
        }

        event.breakStartArray[index] = this.getDateValueObject(
          automatedBreak.breakStart || emptyValue
        );
        event.breakEndArray[index] = this.getDateValueObject(automatedBreak.breakEnd || emptyValue);

        event.breakDurationArray[index] = {
          value: breakDurationInMinutes || emptyValue,
          ...(projectionStatus === TimesheetStatus.inProgress
            ? {}
            : {
                violations: automatedBreak.violations,
                isMissed: automatedBreak.isMissed,
                isExcessive: automatedBreak.isExcessive,
                isPremiumPay: automatedBreak.isPremiumPay,
                attestationOption: automatedBreak.attestationOption,
                breakType: automatedBreak.breakType,
              }),
        };
      });
    }
  };

  // eslint-disable-next-line complexity
  fillInClockAndBreakArrays = (event, group, projectionIndex, allBreaksLists) => {
    const projection = event.timesheetProjections[projectionIndex];

    event.clockedInArray.push({
      ...this.getDateValueObject(
        group.clockInEntry ? group.clockInEntry.timestamp : EMPTY_CELL_MARK
      ),
      clockInCoordinates: group.clockInEntry?.coordinates,
    });
    event.clockedOutArray.push({
      ...this.getDateValueObject(
        group.clockOutEntry ? group.clockOutEntry.timestamp : EMPTY_CELL_MARK
      ),
      clockOutCoordinates: group.clockOutEntry?.coordinates,
    });
    event.actualDurationArray.push({
      value:
        projection.status === TimesheetStatus.inProgress ? EMPTY_CELL_MARK : projection.paidMinutes,
    });

    let isAutoClockOutValue = '';
    let isAutoClockOutId = null;
    if (group.clockOutEntry) {
      isAutoClockOutValue =
        group.clockOutEntry.type === TimesheetEntryType.autoClockOut
          ? gettext('Yes')
          : gettext('No');

      isAutoClockOutId = group.clockOutEntry.type === TimesheetEntryType.autoClockOut;
    }

    event.isAutoClockOutArray.push({
      value: isAutoClockOutValue,
      id: isAutoClockOutId,
    });

    this.fillInForAutomatedBreaks(event, allBreaksLists[projectionIndex], projection.status);
    this.fillInForStandardBreaks(event, group, projection.status);
  };

  getAutomatedBreaksSettingsForEvent = (event) => {
    const automatedBreaksSettings = mapAPIAutomatedBreaksSettingsToUI(
      this.Authentication.user.orgSettings
    );
    if (event.timesheetEntries.length) {
      const firstClockIn = event.timesheetEntries[0];
      automatedBreaksSettings.meal.isEnabled = firstClockIn.automatedBreakSettingMealIsEnabled;
      automatedBreaksSettings.meal.isPaid = firstClockIn.automatedBreakSettingMealIsPaid
        ? AutomatedBreakPaidCategory.paid
        : AutomatedBreakPaidCategory.unpaid;
      automatedBreaksSettings.meal.breakDuration = firstClockIn.automatedBreakSettingMealDuration;
      automatedBreaksSettings.meal.breakThreshold = firstClockIn.automatedBreakSettingMealThreshold;
      automatedBreaksSettings.rest.isEnabled = firstClockIn.automatedBreakSettingRestIsEnabled;
      automatedBreaksSettings.rest.isPaid = firstClockIn.automatedBreakSettingRestIsPaid
        ? AutomatedBreakPaidCategory.paid
        : AutomatedBreakPaidCategory.unpaid;
      automatedBreaksSettings.rest.breakDuration = firstClockIn.automatedBreakSettingRestDuration;
      automatedBreaksSettings.rest.breakThreshold = firstClockIn.automatedBreakSettingRestThreshold;
      automatedBreaksSettings.isEnabled =
        firstClockIn.automatedBreakSettingMealIsEnabled ||
        firstClockIn.automatedBreakSettingRestIsEnabled;
    }

    return automatedBreaksSettings;
  };

  accountPaidDurationForScheduledAutomatedBreaks = (event) => {
    const automatedBreaksSettings = this.getAutomatedBreaksSettingsForEvent(event);
    const scheduledAutomatedBreaks = getScheduledBreaks({ shift: event, automatedBreaksSettings });

    if (automatedBreaksSettings.isEnabled) {
      event.paidDurationMinutes = event.duration * 60;
      if (automatedBreaksSettings.meal.isPaid === AutomatedBreakPaidCategory.unpaid) {
        event.paidDurationMinutes -= scheduledAutomatedBreaks.mealTotalDuration;
      }

      if (automatedBreaksSettings.rest.isPaid === AutomatedBreakPaidCategory.unpaid) {
        event.paidDurationMinutes -= scheduledAutomatedBreaks.restTotalDuration;
      }
    }
  };
}

export default TimesheetsService;
